import type { TelegramPort } from './telegram-port';

/** Events of `WebApp.onEvent` that FXMate listens to. */
type TelegramEvent = 'themeChanged' | 'activated' | 'deactivated';

/** `WebApp.MainButton` of telegram-web-app.js, the subset FXMate uses (C3 R15). */
interface TelegramMainButton {
  setParams(params: { text: string; is_active: boolean; is_visible: boolean }): void;
  hide(): void;
  showProgress(leaveActive?: boolean): void;
  hideProgress(): void;
  onClick(handler: () => void): void;
  offClick(handler: () => void): void;
}

/** `WebApp.BackButton` of telegram-web-app.js (C3 R15). */
interface TelegramBackButton {
  show(): void;
  hide(): void;
  onClick(handler: () => void): void;
  offClick(handler: () => void): void;
}

/** The tap API both native buttons share. */
type TappableButton = Pick<TelegramBackButton, 'onClick' | 'offClick'>;

/** The subset of the official `window.Telegram.WebApp` object that FXMate uses. */
export interface TelegramWebApp {
  initData: string;
  initDataUnsafe: { user?: { language_code?: string }; start_param?: string };
  colorScheme?: 'light' | 'dark';
  /** Bot API 8.0; absent on older clients, read as active. */
  isActive?: boolean;
  /** Optional: absent from test doubles; every Telegram client has them. */
  MainButton?: TelegramMainButton;
  BackButton?: TelegramBackButton;
  ready(): void;
  /** Optional: absent from very old clients and from test doubles. */
  onEvent?(eventType: TelegramEvent, handler: () => void): void;
  offEvent?(eventType: TelegramEvent, handler: () => void): void;
  /** Bot API 6.1 and later; absent from test doubles. */
  openTelegramLink?(url: string): void;
  /** Optional: Bot API 6.9 and later. The second argument's shape is assumed, see `contactPhone`. */
  requestContact?(callback?: (shared: boolean, event?: unknown) => void): void;
}

/** The window as the adapter sees it: `Telegram` is absent outside the Telegram client. */
export interface TelegramWindow {
  Telegram?: { WebApp?: TelegramWebApp };
}

const E164 = /^\+[1-9]\d{6,14}$/;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function toE164(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const candidate = `+${value.replaceAll(/\D/g, '')}`;
  return E164.test(candidate) ? candidate : null;
}

/**
 * Reads the number out of what Telegram passes after a contact request. Q70, verify on the first
 * run: Telegram's documentation decides whether the Mini App gets the number at all. Two shapes
 * are read, `{ responseUnsigned: { contact: { phone_number } } }` and a signed query string
 * `{ response: 'query_id=…&contact=<json>&hash=…' }`; anything else gives `null`.
 */
function contactPhone(event: unknown): string | null {
  if (!isRecord(event)) return null;
  const unsigned = event['responseUnsigned'];
  if (isRecord(unsigned) && isRecord(unsigned['contact'])) {
    return toE164(unsigned['contact']['phone_number']);
  }
  const response = event['response'];
  if (typeof response !== 'string') return null;
  const raw = new URLSearchParams(response).get('contact');
  if (raw === null) return null;
  try {
    const contact: unknown = JSON.parse(raw);
    return isRecord(contact) ? toE164(contact['phone_number']) : null;
  } catch {
    return null;
  }
}

const noop = (): void => undefined;

/**
 * Subscribes `listener` to the taps of a native button and returns the unsubscribe; a no-op when
 * the client has no such button. A wrapper is subscribed, so the same function subscribed twice
 * gets two independent subscriptions, and `offClick` removes the very handler that was added.
 */
function listenButton(button: TappableButton | undefined, listener: () => void): () => void {
  if (button === undefined) return noop;
  const handler = (): void => {
    listener();
  };
  button.onClick(handler);
  return () => {
    button.offClick(handler);
  };
}

/**
 * Adapter over the official telegram-web-app.js script (ADR 0004).
 *
 * `initDataUnsafe` is used only for display hints such as the language: it is not
 * verified. The API always re-verifies the signed `initData` (master X1).
 */
export function createWebAppTelegramPort(win: TelegramWindow): TelegramPort {
  const webApp = win.Telegram?.WebApp;

  /** Subscribes `handler` to a Telegram event and returns the unsubscribe; no-op outside Telegram. */
  function listen(event: TelegramEvent, handler: () => void): () => void {
    if (webApp?.onEvent === undefined) return noop;
    webApp.onEvent(event, handler);
    return () => {
      webApp.offEvent?.(event, handler);
    };
  }

  return {
    initDataRaw: () =>
      webApp === undefined || webApp.initData === '' ? undefined : webApp.initData,
    languageCode: () => webApp?.initDataUnsafe.user?.language_code,
    startParam: () => webApp?.initDataUnsafe.start_param,
    colorScheme: () => webApp?.colorScheme ?? 'light',
    onColorSchemeChange: (listener) =>
      // Telegram passes no argument: `colorScheme` already holds the new value when it fires.
      listen('themeChanged', () => {
        listener(webApp?.colorScheme ?? 'light');
      }),
    requestPhoneNumber: () =>
      new Promise<string | null>((resolve) => {
        if (webApp?.requestContact === undefined) {
          resolve(null);
          return;
        }
        webApp.requestContact((shared, event) => {
          resolve(shared ? contactPhone(event) : null);
        });
      }),
    openTelegramLink: (url) => {
      webApp?.openTelegramLink?.(url);
    },
    ready: () => {
      webApp?.ready();
    },
    showMainButton: ({ text, enabled, loading }) => {
      const button = webApp?.MainButton;
      if (button === undefined) return;
      // Progress first, params last: showProgress / hideProgress may change the active state on
      // some clients, so setParams has the final word on text, is_active and is_visible.
      // leaveActive false: Telegram also refuses taps while its spinner turns.
      if (loading) button.showProgress(false);
      else button.hideProgress();
      button.setParams({ text, is_active: enabled, is_visible: true });
    },
    hideMainButton: () => {
      webApp?.MainButton?.hideProgress();
      webApp?.MainButton?.hide();
    },
    onMainButtonClick: (listener) => listenButton(webApp?.MainButton, listener),
    showBackButton: () => {
      webApp?.BackButton?.show();
    },
    hideBackButton: () => {
      webApp?.BackButton?.hide();
    },
    onBackButtonClick: (listener) => listenButton(webApp?.BackButton, listener),
    isActive: () => webApp?.isActive ?? true,
    onActiveChange: (listener) => {
      // The event name gives the value: the events carry no argument.
      const offActivated = listen('activated', () => {
        listener(true);
      });
      const offDeactivated = listen('deactivated', () => {
        listener(false);
      });
      return () => {
        offActivated();
        offDeactivated();
      };
    },
  };
}
